import React from "react";
import { FieldRenderer } from "../Questionnarie/Dynamic/FieldRenderer";
import Blackupload from "../../Images/Questionnaire/upload.svg";
import LinkIcon from "../../Images/Questionnaire/icons8-link-26.png";

// Render one dynamic-questionnaire question using the LEGACY 7-page CSS
// (defined in Questionnarie/Questionnaire.css). Keeps the visual identical
// to /questionnaire/2 etc. — class names match legacy components, fonts &
// borders inherit from the legacy stylesheet.
//
// For answer types whose legacy markup is non-trivial (age-data with the
// gender + portraits grid, the bottom-bordered text/textarea, the slider,
// etc.) we hand-write the markup here. For the rest we fall through to
// the dynamic FieldRenderer — those types didn't exist in legacy so there's
// no "legacy look" to match.

const SURPRISE_SENTINEL = "surprise";

const countWords = (s) => {
  if (!s || typeof s !== "string") return 0;
  return s.trim().split(/\s+/).filter(Boolean).length;
};

// Format an English age-band ("10 or Less", "41-60+") into Arabic with the
// correct LTR/RTL handling — mirrors Questionnaire2's formatAgeRange.
const formatAgeRange = (label, lang) => {
  if (lang !== "ar") return label;
  const text = (label || "").trim();
  if (text.toLowerCase().includes("or less")) {
    const number = text.replace(/\s*or\s*less/i, "");
    return `${number} أو أقل`;
  }
  if (text.includes("-") && text.includes("+")) {
    const parts = text.split("-");
    if (parts.length === 2) return `‭${parts[1]}-${parts[0]}‬`;
  }
  if (text.includes("-")) {
    const parts = text.split("-");
    if (parts.length === 2) return `${parts[1]}-${parts[0]}`;
  }
  return text;
};

// Decide whether to render an answer as the female-section, male-section
// or both. Mirrors how Questionnaire2 stores `answer = [{female:bool,
// value: [...]} , {male:bool, value:[...]}]`.
const ageAnswer = (value) => {
  const fallback = [
    { female: false, value: [] },
    { male: false, value: [] },
  ];
  if (!Array.isArray(value)) return fallback;
  return [
    { female: !!value?.[0]?.female, value: value?.[0]?.value || [] },
    { male: !!value?.[1]?.male, value: value?.[1]?.value || [] },
  ];
};

export const LegacyStyledQuestion = ({ question: q, index = 0, value, onChange, lang = "en", error }) => {
  const text = lang === "ar" && q.question_arabic ? q.question_arabic : q.question;
  const placeholder =
    lang === "ar" && q.placeholder_arabic ? q.placeholder_arabic : q.placeholder || "";
  const isSurprised = value === SURPRISE_SENTINEL;
  const wordCount = typeof value === "string" ? countWords(value) : 0;
  const wordLimit = q.max_words || 70; // legacy default cap

  // ---------- Per-type bodies (legacy markup) -------------------------------

  let body = null;

  if (q.answer_type === "input" || q.answer_type === "textarea") {
    // Legacy uses <textarea> for both; bottom-bordered, auto-grow, with the
    // 70-word counter underneath.
    body = (
      <>
        <textarea
          className="question-input border-b-[2px] border-black"
          placeholder={placeholder}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange && onChange(e.target.value)}
          rows={1}
          style={{ width: "100%", resize: "none", overflow: "hidden" }}
        />
        <div
          className={`word-counter ${wordCount > wordLimit ? "limit-exceeded" : ""}`}
        >
          {lang === "ar" ? "كلمات" : "Words"}: {wordCount} / {wordLimit}
        </div>
      </>
    );
  } else if (q.answer_type === "age-data" || q.answer_type === "age_grid") {
    const ans = ageAnswer(value);
    const showFemale = ans[0].female;
    const showMale = ans[1].male;
    // Pull option labels from admin-managed options if any; otherwise fall
    // back to the legacy hardcoded age bands.
    const labels =
      (q.options || []).length > 0
        ? q.options.map((o) => o.label)
        : ["10 or Less", "11-17", "18-23", "24-30", "31-40", "41-60+"];
    const femaleImages = (q.options || []).map((o) => o.image);
    const maleImages = (q.options || []).map((o) => o?.metadata?.image_male);

    const flipGender = (which) => {
      const next = [...ans];
      if (which === "female") next[0] = { ...next[0], female: !next[0].female };
      else next[1] = { ...next[1], male: !next[1].male };
      onChange && onChange(next);
    };
    const toggleAge = (which, label) => {
      const next = [...ans];
      const idx = which === "female" ? 0 : 1;
      const list = next[idx].value.includes(label)
        ? next[idx].value.filter((v) => v !== label)
        : [...next[idx].value, label];
      next[idx] = { ...next[idx], value: list };
      onChange && onChange(next);
    };

    body = (
      <div className="ideal-customers">
        <div style={{ display: "flex", gap: "25px" }} className="mt-[5%]">
          <button
            type="button"
            className={ans[0].female ? "female-active" : "female"}
            onClick={() => flipGender("female")}
          >
            {lang === "ar" ? "أنثى" : "Female"}
          </button>
          <button
            type="button"
            className={ans[1].male ? "male-active" : "male"}
            onClick={() => flipGender("male")}
          >
            {lang === "ar" ? "ذكر" : "Male"}
          </button>
        </div>

        <div className="border-b-[1px] border-solid border-[#000000] mb-4">
          {showFemale ? (
            <div className="female-section mb-[5%]">
              <div className="female-buttons">
                {labels.map((label, idx) => (
                  <div key={`f-${idx}`} style={{ display: "flex", flexDirection: "column" }}>
                    <div className="flex justify-center items-center">
                      {femaleImages[idx] ? (
                        <img
                          src={femaleImages[idx]}
                          alt={label}
                          className="female-image"
                          onClick={() => toggleAge("female", label)}
                        />
                      ) : (
                        <div className="female-image" style={{ background: "#eee" }} />
                      )}
                    </div>
                    <button
                      type="button"
                      className={`female-btn uppercase ${ans[0].value.includes(label) ? "active" : ""}`}
                      onClick={() => toggleAge("female", label)}
                    >
                      {formatAgeRange(label, lang)}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="female-section mb-[5%]" />
          )}

          {showMale ? (
            <div className="male-section">
              <div className="male-buttons">
                {labels.map((label, idx) => (
                  <div key={`m-${idx}`} style={{ display: "flex", flexDirection: "column" }}>
                    <div className="flex justify-center items-center">
                      {maleImages[idx] ? (
                        <img
                          src={maleImages[idx]}
                          alt={label}
                          className="male-image"
                          onClick={() => toggleAge("male", label)}
                        />
                      ) : (
                        <div className="male-image" style={{ background: "#eee" }} />
                      )}
                    </div>
                    <button
                      type="button"
                      className={`male-btn uppercase ${ans[1].value.includes(label) ? "active" : ""}`}
                      onClick={() => toggleAge("male", label)}
                    >
                      {formatAgeRange(label, lang)}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  } else if (q.answer_type === "brand") {
    // Legacy "brand" is a 2-button binary toggle (PRODUCT/SERVICE in
    // legacy Questionnaire1; ARABIC/ENGLISH in legacy Questionnaire4).
    // Stores the selected option's value as a plain string. No textarea.
    const toggles =
      (q.config && Array.isArray(q.config.toggle_options) && q.config.toggle_options.length
        ? q.config.toggle_options
        : [
            { value: "product", label: "PRODUCT", label_arabic: "منتج" },
            { value: "service", label: "SERVICE", label_arabic: "خدمة" },
          ]);
    const sel = typeof value === "string" ? value : "";
    body = (
      <>
        <div style={{ display: "flex", gap: "10px", marginBottom: "3%", justifyContent: "center" }}>
          {toggles.map((opt, i) => {
            const cls = i === 0 ? "product-btn" : "service-btn";
            const label = lang === "ar" && opt.label_arabic ? opt.label_arabic : opt.label;
            return (
              <button
                type="button"
                key={opt.value}
                className={`${cls} ${sel === opt.value ? "active" : ""}`}
                onClick={() => onChange && onChange(opt.value)}
              >
                {label}
              </button>
            );
          })}
        </div>
        <div className="w-[100%] xl:h-[2px] lg:h-[2px] md:h-[2px] sm:h-[2px] xs:h-[1px] bg-black" />
      </>
    );
  } else if (q.answer_type === "thumbnail" || q.answer_type === "brand2") {
    // Legacy "thumbnail" + "brand2" — grid of image cards with labels.
    // Used for capitalisation styles (AA / aa / Aa), illustration formats,
    // logo styles, etc. Stores the picked option's value as a plain string.
    const opts = q.options || [];
    const sel = typeof value === "string" ? value : "";
    body = opts.length === 0 ? (
      <div style={{ textAlign: "center", color: "#888", fontStyle: "italic", padding: 16 }}>
        {lang === "ar"
          ? "لم يتم رفع الخيارات لهذا السؤال بعد."
          : "No options uploaded for this question yet."}
      </div>
    ) : (
      <div className="form-group lg:w-[75%] md:w-[100%]" style={{ margin: "0 auto" }}>
        <ul
          style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)" }}
          className="h-list select-btns grid-view padding-top-20 checkbox-btn-img h-list-check"
        >
          {opts.map((opt) => {
            const checked = sel === opt.value;
            const label = lang === "ar" && opt.label_arabic ? opt.label_arabic : opt.label;
            return (
              <li className="checkbox checkbox-btn !w-full" key={opt.id}>
                <input
                  type="checkbox"
                  checked={checked}
                  value={opt.value}
                  id={`thumb-${q.id}-${opt.id}`}
                  onChange={() => onChange && onChange(opt.value)}
                />
                <label htmlFor={`thumb-${q.id}-${opt.id}`} className="h-48 relative">
                  <figure className="image-container img-animation !h-[80%]">
                    {opt.image ? (
                      <img className="!object-contain" src={opt.image} alt={label} />
                    ) : (
                      // Fallback when admin hasn't uploaded a thumbnail —
                      // render the metadata.preview_text (e.g. "AA" / "aa")
                      // or the label itself as big centred text.
                      <span style={{
                        display: "flex", alignItems: "center", justifyContent: "center",
                        height: "100%", fontSize: 60, fontWeight: 700,
                      }}>
                        {(opt.metadata && opt.metadata.preview_text) || label}
                      </span>
                    )}
                  </figure>
                  <span className="button-text !text-[16px] absolute bottom-0 w-full">
                    {label}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    );
  } else if (q.answer_type === "file/link") {
    // Legacy "file/link" — Links text input + small link-icon button to
    // the right, with an Upload Content pill button below. Mirrors the
    // page-7 (Final Thoughts) "Please provide links / pictures..." UX.
    const v = (value && typeof value === "object") ? value : { link: "", documents: [] };
    const docs = Array.isArray(v.documents) ? v.documents : [];
    const setLink = (s) => onChange && onChange({ ...v, link: s });
    const removeDoc = (i) => {
      const next = docs.filter((_, idx) => idx !== i);
      onChange && onChange({ ...v, documents: next });
    };
    body = (
      <div
        className={`gap-[20px] flex flex-col sm:flex-row w-full justify-center items-start border-b-[2px] border-black`}
      >
        <div
          className="color-input"
          style={{
            marginTop: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            position: "relative",
          }}
        >
          <input
            type="text"
            placeholder={lang === "ar" ? "روابط" : "Links"}
            value={v.link || ""}
            onChange={(e) => setLink(e.target.value)}
            style={{
              padding: 8,
              border: "1px solid #000",
              outline: "none",
              width: 300,
              borderRadius: 0,
            }}
          />
          <div
            className={`word-counter ${countWords(v.link) > wordLimit ? "limit-exceeded" : ""}`}
          >
            {lang === "ar" ? "كلمات" : "Words"}: {countWords(v.link)} / {wordLimit}
          </div>
          <button
            type="button"
            style={{
              padding: "8px 16px",
              backgroundColor: "transparent",
              border: "none",
              cursor: "pointer",
              margin: lang === "ar" ? "-55px 80% 0 0" : "-55px 0 0 80%",
            }}
          >
            <img src={LinkIcon} alt="link" />
          </button>
        </div>

        <p
          className={`border-1 h-[45px] lg:text-[18px] md:text-[18px] xs:text-[14px] uppercase w-[300px] !border-[#000000] flex items-center justify-center text-[#000000] cursor-pointer ml-2 mt-4 p-[5px]`}
        >
          <input
            type="file"
            multiple
            id={`file-${q.id}`}
            style={{ display: "none" }}
          />
          <label
            htmlFor={`file-${q.id}`}
            style={{ display: "flex", alignItems: "center", cursor: "pointer", margin: 0 }}
          >
            <img className="h-[25px] w-[40px]" src={Blackupload} alt="" />
            {lang === "ar" ? "إضافة المحتوى" : "Upload Content"}
          </label>
        </p>

        {docs.length > 0 ? (
          <div className="flex flex-wrap" style={{ width: "100%", marginTop: 8 }}>
            {docs.map((d, i) => (
              <div
                key={i}
                className="bg-black text-[14px] text-white py-1 px-2 mr-2 mb-2 flex items-center"
              >
                {d?.docName || d?.name || "file"}
                <span
                  onClick={() => removeDoc(i)}
                  style={{ marginLeft: 8, cursor: "pointer", fontWeight: 700 }}
                >
                  ×
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  } else if (q.answer_type === "file") {
    // Legacy "file" — just the Upload Content pill button on a bottom-
    // bordered container, with placeholder text shown above when set.
    const v = (value && typeof value === "object") ? value : { documents: [] };
    const docs = Array.isArray(v.documents) ? v.documents : [];
    body = (
      <div className={`question-input border-b-[2px] border-black`}>
        {placeholder ? (
          <p className="text-[#a9a9a9] text-[18px]" style={{ marginTop: -12 }}>
            {placeholder}
          </p>
        ) : null}
        <div className="flex justify-center items-center">
          <p
            className={`border-1 lg:text-[18px] md:text-[18px] xs:text-[14px] uppercase font-Helvetica font-[400] w-[300px] !border-[#000000] flex items-center justify-center text-[#000000] cursor-pointer ml-2 mt-3 p-2`}
          >
            <input
              type="file"
              multiple
              id={`file-only-${q.id}`}
              style={{ display: "none" }}
            />
            <label
              htmlFor={`file-only-${q.id}`}
              style={{ display: "flex", alignItems: "center", cursor: "pointer", margin: 0 }}
            >
              <img className="h-[25px] w-[40px]" src={Blackupload} alt="" />
              {lang === "ar" ? "تحميل المحتوى" : "Upload Content"}
            </label>
          </p>
        </div>
        {docs.length > 0 ? (
          <div className="flex flex-wrap justify-center" style={{ marginTop: 8 }}>
            {docs.map((d, i) => (
              <div
                key={i}
                className="bg-black text-[14px] text-white py-1 px-2 mr-2 mb-2 flex items-center"
              >
                {d?.docName || d?.name || "file"}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  } else {
    // Other answer types — defer to the dynamic FieldRenderer. These types
    // didn't exist in the legacy 7-page flow so there is no legacy look to
    // mirror; the dynamic components already match production styling.
    body = (
      <FieldRenderer
        question={q}
        value={value}
        onChange={onChange || (() => {})}
        lang={lang}
      />
    );
  }

  return (
    <div
      className="questions"
      key={q.id}
      id={`question_${q.id}`}
    >
      <p
        className={`questions-title xs:w-[90%] sm:w-full md:w-full mx-auto ${
          index === 0 ? "mt-[1%]" : "mt-[2%]"
        }`}
      >
        {text}
        {q.required ? (
          <span>
            <sup>*</sup>
          </span>
        ) : null}
      </p>

      {isSurprised ? (
        <div
          style={{
            padding: 12,
            background: "#fff7d6",
            borderRadius: 6,
            color: "#7a5e00",
            fontSize: 14,
            margin: "0 auto",
            maxWidth: 600,
          }}
        >
          {lang === "ar"
            ? "ستقوم لجنة التصميم بالاختيار نيابة عنك."
            : "The design team will pick this for you."}{" "}
          <button
            type="button"
            style={{
              marginInlineStart: 12,
              background: "transparent",
              border: "none",
              color: "#7a5e00",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={() => onChange && onChange(null)}
          >
            {lang === "ar" ? "تراجع" : "Undo"}
          </button>
        </div>
      ) : (
        body
      )}

      {q.allow_surprise && !isSurprised ? (
        <div style={{ textAlign: "center", marginTop: 12 }}>
          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
            {lang === "ar" ? "غير متأكد؟ لا بأس!" : "Not sure? It's okay!"}
          </div>
          <button
            type="button"
            onClick={() => onChange && onChange(SURPRISE_SENTINEL)}
            style={{
              padding: "6px 18px",
              border: "1px solid #ccc",
              background: "#fff",
              borderRadius: 4,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {lang === "ar" ? "فاجئني!" : "SURPRISE ME!"}
          </button>
        </div>
      ) : null}

      {error ? <p className="text-[#D83D99]">{error}</p> : null}
    </div>
  );
};
